import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Service } from '../../model/professional/service-model';
import { useServiceFormViewModel } from '../../view-model/professional/company/service-form-view-model';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import FormField from '../ui/form-field';
import PriceField from '../ui/price-field';
import DurationField from '../ui/duration-field';
import Switch from '../ui/switch';
import InputImage from '../ui/input-image';
import CollaboratorSelector from './collaborator-selector';
import ConfirmationModal from '../ui/confirmation-modal';
import companyStore from '../../store/company-store';
import subscriptionStore from '../../store/subscription-store';

interface ServiceFormProps {
  mode: 'create' | 'edit' | 'view';
  service?: Service;
  onSuccess: (serviceId?: string | null) => void;
}

const ServiceForm = observer((props: ServiceFormProps) => {
  const { mode, service, onSuccess } = props;
  const viewModel = useServiceFormViewModel();
  const { t } = useTranslation();
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: ''
  });

  useEffect(() => {
    viewModel.initializeForm(service || null);
    return () => {
      viewModel.resetForm();
    };
  }, [service?.id]);

  // Surveiller les changements de snackbar et les convertir en modal
  useEffect(() => {
    if (viewModel.snackbarState.open) {
      setModalState({
        isOpen: true,
        type: viewModel.snackbarState.severity as 'success' | 'error' | 'warning' | 'info',
        title: viewModel.snackbarState.severity === 'success' ? 'Succès' :
               viewModel.snackbarState.severity === 'error' ? 'Erreur' :
               viewModel.snackbarState.severity === 'warning' ? 'Attention' : 'Information',
        message: viewModel.snackbarState.message
      });
      viewModel.closeSnackbar();
    }
  }, [viewModel.snackbarState.open]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      let serviceId = null;
  
      if (mode === 'create') {
        serviceId = await viewModel.submitCreateForm(e.currentTarget);
      } else if (mode === 'edit' && service) {
        await viewModel.submitUpdateForm(service.id, e.currentTarget);
        serviceId = service.id;
      }
  
      if (serviceId) {
        onSuccess(serviceId);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  // Si en mode vue, désactiver tous les champs
  const isViewMode = mode === 'view';
  const isDisabled = isViewMode || viewModel.isLoading;

  return (
    <form onSubmit={handleSubmit} className="h-full flex flex-col">
      <div className="flex-1 overflow-auto">
        <div className="p-6 space-y-5">
          {/* Informations principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <FormField
                autoFocus
                id="name"
                name="name"
                label={t('services.serviceName')}
                defaultValue={service?.name}
                placeholder={t('services.serviceName') || ''}
                required
                disabled={isDisabled}
                maxLength={60}
              />
            </div>

            <div className="md:col-span-2">
              <FormField
                id="description"
                name="description"
                label={t('services.serviceDescription')}
                type="textarea"
                defaultValue={service?.description}
                placeholder={t('services.serviceDescription') || ''}
                disabled={isDisabled}
                maxLength={155}
              />
            </div>

            <PriceField
              id="price"
              name="price"
              label={t('services.price')}
              defaultValue={service?.price}
              required
              disabled={isDisabled}
            />

            {viewModel.requiresBooking && (
              <DurationField
                id="duration"
                name="duration"
                label={t('services.duration')}
                defaultValue={service?.duration}
                required={true}
                disabled={isDisabled}
              />
            )}
          </div>

          {/* Image */}
          <div>
            <InputImage
              id="image"
              name="image"
              label={t('services.serviceImage')}
              imagePreview={viewModel.imagePreview}
              onImageChange={(file) => viewModel.handleImageChange(file)}
              onRemoveImage={() => viewModel.handleRemoveImage()}
              disabled={isDisabled}
            />
          </div>

          {/* Paramètres de réservation */}
          {viewModel.requiresBooking && (
            <>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">PARAMÈTRES DE RÉSERVATION</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    id="minTimeBeforeBooking"
                    name="minTimeBeforeBooking"
                    type="number"
                    label={t('services.minTimeBeforeBooking') || 'Délai minimum (min)'}
                    defaultValue={service?.minTimeBeforeBooking || ''}
                    placeholder="120"
                    min={0}
                    disabled={isDisabled}
                    help={t('services.minTimeBeforeBookingHelp') || 'Délai minimum avant réservation'}
                  />

                  <FormField
                    id="bufferTimeBetweenSlots"
                    name="bufferTimeBetweenSlots"
                    type="number"
                    label={t('services.bufferTimeBetweenSlots') || 'Temps tampon (min)'}
                    defaultValue={service?.bufferTimeBetweenSlots || ''}
                    placeholder="10"
                    min={0}
                    disabled={isDisabled}
                    help={t('services.bufferTimeBetweenSlotsHelp') || 'Temps entre rendez-vous'}
                  />
                </div>
              </div>
            </>
          )}

          {/* Configuration */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">CONFIGURATION</h4>
            <div className="space-y-3">
              <Switch
                id="requiresBooking"
                name="requiresBooking"
                label={t('services.requiresBooking')}
                defaultChecked={viewModel.requiresBooking}
                onChange={(checked) => viewModel.setRequiresBooking(checked)}
                disabled={isDisabled}
              />

              <Switch
                id="isActive"
                name="isActive"
                label={t('services.isActive')}
                defaultChecked={service?.isActive ?? true}
                disabled={isDisabled}
              />
            </div>
          </div>

          {/* Politique d'annulation - Affiché uniquement si l'abonnement permet les acomptes */}
          {viewModel.requiresBooking && subscriptionStore.hasFeature('depositPayment') && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
              <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">POLITIQUE D'ANNULATION</h4>
              <div className="space-y-4">
                <FormField
                  id="cancellationRefundDeadlineHours"
                  name="cancellationRefundDeadlineHours"
                  type="number"
                  label="Délai d'annulation gratuite (heures)"
                  defaultValue={service?.cancellationRefundDeadlineHours || 24}
                  placeholder="24"
                  min={0}
                  step={1}
                  disabled={isDisabled}
                  help="Le client peut annuler gratuitement jusqu'à X heures avant le rendez-vous. Si un acompte a été versé, il sera automatiquement remboursé."
                />
              </div>
            </div>
          )}

          {/* Configuration des acomptes - Masqué pour le plan Junior */}
          {subscriptionStore.hasFeature('depositPayment') && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
              <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">ACOMPTE</h4>
              <div className="space-y-4">
                <Switch
                  id="depositRequired"
                  name="depositRequired"
                  label="Acompte obligatoire"
                  defaultChecked={service?.depositRequired ?? false}
                  onChange={(checked) => viewModel.setDepositRequired(checked)}
                  disabled={isDisabled}
                />

                {viewModel.depositRequired && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Type d'acompte
                        </label>
                        <select
                          id="depositType"
                          name="depositType"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                          defaultValue={service?.depositType || 'percentage'}
                          onChange={(e) => viewModel.setDepositType(e.target.value as 'percentage' | 'fixed')}
                          disabled={isDisabled}
                        >
                          <option value="percentage">Pourcentage (%)</option>
                          <option value="fixed">Montant fixe (€)</option>
                        </select>
                      </div>

                      <FormField
                        id="depositValue"
                        name="depositValue"
                        type="number"
                        label={viewModel.depositType === 'percentage' ? 'Pourcentage (%)' : 'Montant (€)'}
                        defaultValue={service?.depositValue || ''}
                        placeholder={viewModel.depositType === 'percentage' ? '30' : '20'}
                        min={viewModel.depositType === 'percentage' ? 1 : 0.01}
                        max={viewModel.depositType === 'percentage' ? 100 : undefined}
                        step={viewModel.depositType === 'percentage' ? 1 : 0.01}
                        required={viewModel.depositRequired}
                        disabled={isDisabled}
                      />
                    </div>

                    {viewModel.depositType === 'percentage' && viewModel.depositValue && (
                      <div className="pl-6 p-3 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30 border border-violet-200 dark:border-violet-800/50 rounded-lg">
                        <p className="text-sm text-violet-700 dark:text-violet-300">
                          Le client devra payer <strong>{viewModel.depositValue}%</strong> du prix total lors de la réservation
                        </p>
                      </div>
                    )}

                    {viewModel.depositType === 'fixed' && viewModel.depositValue && (
                      <div className="pl-6 p-3 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30 border border-violet-200 dark:border-violet-800/50 rounded-lg">
                        <p className="text-sm text-violet-700 dark:text-violet-300">
                          Le client devra payer <strong>{viewModel.depositValue}€</strong> lors de la réservation
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Collaborateurs */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">COLLABORATEURS</h4>
            <CollaboratorSelector
              collaborators={companyStore.getDefaultCompanyCollaborators}
              selectedIds={viewModel.selectedCollaboratorIds}
              onToggle={(id) => viewModel.toggleCollaborator(id)}
              disabled={isDisabled}
              label={t('services.assignCollaborators') || 'Assigner des collaborateurs'}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {t('services.assignCollaboratorsHelp') || 'Sélectionnez les collaborateurs'}
            </p>
          </div>
        </div>
      </div>

      {/* Footer avec bouton fixe */}
      {!isViewMode && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={isDisabled}
              className="min-w-[120px]"
            >
              {viewModel.isLoading ? t('services.loading') : mode === 'create' ? t('services.create') : t('services.save')}
            </Button>
          </div>
        </div>
      )}

      {/* Modal pour les notifications */}
      <ConfirmationModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ ...modalState, isOpen: false })}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        showCancel={false}
        confirmText="OK"
      />
    </form>
  );
});

export default ServiceForm;
